import { DataAccess } from "../../dataaccess/data.access";
import { ProgressResponse, RequirementRootDetail } from "../student-engagement/trails/trail.api";

export interface AcademicTerm {
    termId?: number;
    termCode: string;
    description?: string;
    startDt?: string;
    endDt?: string;
}

export interface AcademicCohort {
    cohortId: number;
    cohortCode: string;
    cohortTypeCode: string;
    effectiveStartDt?: string;
    effectiveEndDt?: string;
}

export interface AcademicCourse {
    courseId: number;
    cohortId: number;
    courseCode: string;
    subject?: string;
    level?: number;
    title?: string;
    description?: string;
    creditMin?: number;
    creditMax?: number;
    distinctTopic?: boolean;
    status?: string;
    sisId?: string;
    requisiteRootGroupId?: number;
}

export interface AcademicProgram {
    defnId: number;
    cohortId: number;
    name?: string;
    displayName?: string;
    endingAwardKey?: string;
    programKind?: string;
    sisId?: string;
    rootGroupId?: number;
}

export interface AcademicCompletion {
    completionId?: number;
    contId?: number;
    canonicalCourseId?: number;
    termId?: number;
    offeringKey?: string;
    topicTitle?: string;
    grade?: string;
    credits?: number;
    isInProgress?: boolean;
    subject?: string;
    courseCode?: string;
    termCode?: string;
}

export interface AcademicFact {
    factId?: number;
    contId?: number;
    factCode?: string;
    factValue?: string;
    numericValue?: number;
    termId?: number;
}

export interface AcademicTermRow {
    termCode?: string;
    description?: string;
    startDt?: string;
    endDt?: string;
}

export interface AcademicCohortRow {
    cohortCode?: string;
    typeCode?: string;
    startDt?: string;
    endDt?: string;
}

export interface AcademicCourseRow {
    cohortCode?: string;
    typeCode?: string;
    courseCode?: string;
    subject?: string;
    level?: number;
    title?: string;
    description?: string;
    creditMin?: number;
    creditMax?: number;
    distinctTopic?: boolean;
    status?: string;
    organizationName?: string;
    sisId?: string;
}

export interface AcademicOutcomeRow {
    cohortCode?: string;
    typeCode?: string;
    courseCode?: string;
    outcomeCode?: string;
    outcomeText?: string;
}

export interface AcademicComponentRow {
    cohortCode?: string;
    typeCode?: string;
    courseCode?: string;
    componentCode?: string;
    description?: string;
}

export interface AcademicAttributeRow {
    cohortCode?: string;
    typeCode?: string;
    courseCode?: string;
    attributeCode?: string;
}

export interface AcademicEquivalencyRow {
    cohortCode?: string;
    typeCode?: string;
    courseCode?: string;
    equivalentCourseCode?: string;
}

export interface AcademicProgramRow {
    cohortCode?: string;
    typeCode?: string;
    programCode?: string;
    kind?: string;
    name?: string;
    sisId?: string;
}

export interface AcademicRequirementRow {
    cohortCode?: string;
    typeCode?: string;
    ownerKind?: string;
    ownerCode?: string;
    programKind?: string;
    groupKey?: string;
    parentGroupKey?: string;
    operator?: string;
    nRequired?: number;
    minCredits?: number;
    maxCredits?: number;
    groupName?: string;
    leafType?: string;
    courseCode?: string;
    subject?: string;
    levelMin?: number;
    levelMax?: number;
    minGrade?: string;
    corequisite?: boolean;
    disallowPassNoPass?: boolean;
    prohibited?: boolean;
    factCode?: string;
    requiredValue?: string;
    minNumericValue?: number;
    excludeCourseCodes?: string;
}

export interface AcademicCompletionRow {
    email?: string;
    sisId?: string;
    subject?: string;
    courseCode?: string;
    termCode?: string;
    offeringKey?: string;
    topicTitle?: string;
    grade?: string;
    credits?: number;
    inProgress?: boolean;
}

export interface AcademicDeclarationRow {
    email?: string;
    career?: string;
    catalogYear?: string;
    programSisId?: string;
}

export interface AcademicFactRow {
    email?: string;
    factCode?: string;
    termCode?: string;
    value?: string;
}

export type TreeMergeResolutionPolicy = "OURS" | "SIS" | "REVIEW";

export interface TreeMergeResolution {
    resolutionId?: number;
    scope?: string;
    resolution?: TreeMergeResolutionPolicy;
}

export interface TreeMergeConflict {
    conflictId?: number;
    rootGroupId?: number;
    nodeKey?: string;
    fieldName?: string;
    baseValue?: string;
    storedValue?: string;
    incomingValue?: string;
    status?: string;
}

export interface AcademicWorkbook {
    terms?: AcademicTermRow[];
    cohorts?: AcademicCohortRow[];
    courses?: AcademicCourseRow[];
    outcomes?: AcademicOutcomeRow[];
    components?: AcademicComponentRow[];
    attributes?: AcademicAttributeRow[];
    equivalencies?: AcademicEquivalencyRow[];
    programs?: AcademicProgramRow[];
    requirements?: AcademicRequirementRow[];
    completions?: AcademicCompletionRow[];
    facts?: AcademicFactRow[];
    declarations?: AcademicDeclarationRow[];
    errors?: string[];
}

export class AcademicCurricApi {
    private static basePath(): string {
        return "/academic";
    }

    static listTerms(): Promise<AcademicTerm[]> {
        return DataAccess.get<AcademicTerm[]>(this.basePath() + "/terms.json");
    }

    static saveTerm(term: AcademicTerm): Promise<AcademicTerm> {
        return DataAccess.post<AcademicTerm>(this.basePath() + "/terms.json", term);
    }

    static listCohorts(): Promise<AcademicCohort[]> {
        return DataAccess.get<AcademicCohort[]>(this.basePath() + "/cohorts.json");
    }

    static listCourses(cohortId: number): Promise<AcademicCourse[]> {
        return DataAccess.get<AcademicCourse[]>(this.basePath() + `/cohorts/${cohortId}/courses.json`);
    }

    static listPrograms(cohortId: number): Promise<AcademicProgram[]> {
        return DataAccess.get<AcademicProgram[]>(this.basePath() + `/cohorts/${cohortId}/programs.json`);
    }

    static getRequirementRoot(rootGroupId: number): Promise<RequirementRootDetail> {
        return DataAccess.get<RequirementRootDetail>(this.basePath() + `/requirement-roots/${rootGroupId}.json`);
    }

    static listCompletions(contId: number): Promise<AcademicCompletion[]> {
        return DataAccess.get<AcademicCompletion[]>(this.basePath() + `/students/${contId}/completions.json`);
    }

    static listFacts(contId: number, termId?: number): Promise<AcademicFact[]> {
        const query = DataAccess.params({ termId });
        const suffix = query ? `?${query}` : "";
        return DataAccess.get<AcademicFact[]>(this.basePath() + `/students/${contId}/facts.json${suffix}`);
    }

    static evaluateCourse(courseId: number, contId: number, termId?: number): Promise<ProgressResponse> {
        return DataAccess.get<ProgressResponse>(
            this.basePath() + `/courses/${courseId}/evaluate.json?${DataAccess.params({ contId, termId })}`,
        );
    }

    static evaluateProgram(defnId: number, contId: number, termId?: number): Promise<ProgressResponse> {
        return DataAccess.get<ProgressResponse>(
            this.basePath() + `/programs/${defnId}/evaluate.json?${DataAccess.params({ contId, termId })}`,
        );
    }

    static exportWorkbook(): Promise<AcademicWorkbook> {
        return DataAccess.get<AcademicWorkbook>(this.basePath() + "/workbook.json");
    }

    static importWorkbook(workbook: AcademicWorkbook): Promise<AcademicWorkbook> {
        return DataAccess.post<AcademicWorkbook>(
            this.basePath() + "/workbook.json",
            this.wrapRoot("AcademicWorkbookDoc", workbook),
        );
    }

    static getTreeMergeResolution(): Promise<TreeMergeResolution> {
        return DataAccess.get<TreeMergeResolution>(this.basePath() + "/tree-merge-resolution.json");
    }

    static saveTreeMergeResolution(resolution: TreeMergeResolution): Promise<TreeMergeResolution> {
        return DataAccess.post<TreeMergeResolution>(this.basePath() + "/tree-merge-resolution.json", resolution);
    }

    static listConflicts(rootGroupId: number): Promise<TreeMergeConflict[]> {
        return DataAccess.get<TreeMergeConflict[]>(
            this.basePath() + `/requirement-roots/${rootGroupId}/conflicts.json`,
        );
    }

    static resolveConflict(rootGroupId: number, conflictId: number, takeIncoming: boolean): Promise<TreeMergeConflict> {
        return DataAccess.post<TreeMergeConflict>(
            this.basePath() + `/requirement-roots/${rootGroupId}/conflicts/${conflictId}/resolve.json`,
            this.wrapRoot("CurricTreeMergeResolveRequest", { takeIncoming }),
        );
    }

    private static wrapRoot<T extends object>(rootName: string, body: T): Record<string, T> {
        return { [rootName]: body };
    }
}
